import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { useDevSettings } from 'src/pages/developer-settings.page'
import { useGetOrg } from 'src/utils/hooks/use-get-org'
import { useIsEnterpriseOrgMember } from 'src/utils/hooks/use-is-org-member'
import { useTrackLastVisited } from 'src/utils/hooks/use-track-last-visited'
import { userState } from 'src/utils/state'

import Drawer from '../containers/drawer'
import Icon from '../icons/icon'
import { HomeSideNav } from './side-nav-home'
import { UniDropdown } from './uni-dropdown'
import { UserDropdown } from './user-dropdown'

export function Logo({ light = false }) {
  return <img className='w-32' src={light ? '/logo-light.svg' : '/logo.svg'} alt='Vendia' />
}

export type DefaultNavBarProps = {
  showUniDropdown?: boolean
}

const NavBar = ({ showUniDropdown = true }: DefaultNavBarProps) => {
  // Keep track of last visited node and uni to redirect user there on login
  // Need to stick this into a component that's always rendered in the app to pick
  // up the uni/node params from the URL when available
  useTrackLastVisited()

  const { devSettings } = useDevSettings()
  const [user] = useRecoilState<any>(userState)
  const getOrg = useGetOrg()
  const org = getOrg?.data?.getOrganization
  const { data: isEnterprise } = useIsEnterpriseOrgMember()

  // const theme = useVendiaTheme()
  const theme = { name: 'light' } // in case we start using the themes again one day
  const isDarkTheme = theme.name === 'dark'

  let notice
  if (user && user.email_verified === false && !window.location.pathname.match(/\/settings\/email/)) {
    notice = (
      <div style={{ marginLeft: 20 }}>
        <Link to='/settings/email'>
          Notice: Please verify your email &nbsp;
          <strong>{user.email}</strong>
        </Link>
      </div>
    )
  }

  const navClasses = clsx(
    'relative z-30 flex items-center justify-between px-6 py-4',
    !isDarkTheme && 'bg-white',
    isDarkTheme && 'from-primaryCore to-purple-1300 bg-gradient-to-r',
  )

  const [showNavDrawerButton, setShowNavDrawerButton] = useState(false)
  const [showNavDrawer, setShowNavDrawer] = useState(false)
  useEffect(() => {
    // setShowNavDrawer to false if the route changes (user clicked a link)
    setShowNavDrawer(false)

    // Only show the menu button on the /uni/* routes
    setShowNavDrawerButton(window.location.pathname.startsWith('/uni'))
  }, [window.location.pathname])

  return (
    <nav className={navClasses}>
      <div className='flex items-center gap-4'>
        {showNavDrawerButton && (
          <button
            className='hover:bg-tint hover:text-brand -ml-2 flex items-center justify-center rounded-full p-2 transition-colors duration-300'
            onClick={() => setShowNavDrawer(!showNavDrawer)}
            data-testid='nav-drawer-button'
            aria-label='Open top-level navigation menu'
          >
            <Icon name='lucide-menu' size={24} />
          </button>
        )}
        <span className='pl-1'>
          <Link to={devSettings.homeButtonLinksToUniList ? '/uni' : '/'} data-testid='navbar-home-link'>
            <Logo light={isDarkTheme} />
          </Link>
        </span>
        {notice}
      </div>
      <div className='flex items-center gap-6'>
        {/* Non-enterprise tier === non-paying customer which means they can only access onboarding or data-products screens, hide uni nav */}
        {showUniDropdown && isEnterprise ? <UniDropdown /> : null}
        <UserDropdown />
      </div>
      <Drawer open={showNavDrawer} onClose={() => setShowNavDrawer(false)} side='left' className='w-56'>
        <div className='grid place-items-end p-4'>
          <button
            className='hover:bg-tint hover:text-brand flex items-center justify-center rounded-full p-2 transition-colors duration-300'
            onClick={() => setShowNavDrawer(!showNavDrawer)}
            aria-label='Close top-level navigation menu'
          >
            <Icon name='close' size={14} />
          </button>
        </div>
        <HomeSideNav hideBorder />
      </Drawer>
    </nav>
  )
}

export default NavBar
